import { useState, useEffect } from 'react';
import { usePermission } from '../context/PermissionContext';
import { permissionService } from '../services/permissionService';

/**
 * Hook to check if a user has specific page permissions
 * Checks both context permissions and stage permissions from localStorage
 * @example
 * // Check if user has read permission for dashboard
 * const canReadDashboard = usePagePermission('dashboard', 'read');
 */
const usePagePermission = (pageName: string, action: 'read' | 'write' | 'delete') => {
  const { hasPermission, isSuperAdmin } = usePermission();
  const [stagePermissions, setStagePermissions] = useState<string[]>([]);

  useEffect(() => {
    const loadStagePermissions = () => {
      try {
        const stored = localStorage.getItem('stagePermissions');
        if (stored) {
          const parsed = JSON.parse(stored);
          setStagePermissions(Array.isArray(parsed) ? parsed : []);
        } else {
          setStagePermissions([]);
        }
      } catch (error) {
        console.error('Failed to load stage permissions in usePagePermission:', error);
        setStagePermissions([]);
      }
    };

    loadStagePermissions();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'stagePermissions') {
        loadStagePermissions();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  if (isSuperAdmin) {
    return true;
  }

  if (stagePermissions.length > 0) {
    const permissionName = `${pageName}.${action}`;

    for (const permission of stagePermissions) {
      if ((permission as any).name === permissionName) {
        return true;
      }
    }

    const hasNamePermission = permissionService.hasPermissionByName(stagePermissions, permissionName);
    if (hasNamePermission) {
      return true;
    }

  }

  const hasContextPermission = hasPermission(pageName, action);
  if (hasContextPermission) {
    return true;
  }



  return false;
};

export default usePagePermission;
